import * as React from 'react'
import { ReactElement, useContext, useEffect } from 'react'
import { Field, Input, LabelProps } from '@fluentui/react-components'
import { LegalAidCertificateDto } from './LegalAidCertificateForm'
import { useFormikContext } from 'formik'
import { InfoLabel } from '@fluentui/react-components/unstable'
import { LegalAidCertificatePanelProps } from './FormContainer'
import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import { FilePartyRole } from '../../../config/filePartyConfig'
import LocalDatePicker from '../../DatePicker/LocalDatePicker'
import LegalAidBoardCombobox from '../../Combobox/LegalAidBoardCombobox'
import FilePartyCombobox from '../../../v2/components/Combobox/FilePartyCombobox'

export default function BaseDataPanel(props: LegalAidCertificatePanelProps): ReactElement {
    const { values, setFieldValue, errors } = useFormikContext<LegalAidCertificateDto>()
    const {
        settings: { legalAidCertificate: settings },
    } = useContext<GlobalContextType>(GlobalContext)

    useEffect(() => {
        setFieldValue('legalAidBoardPivotId', props.parties.data.find(party => party.id === props.selected.legalAidBoardPartyId)?.pivotId)
    }, [props.selected.legalAidBoardPartyId])

    return (
        <div className={props.styles.formRow}>
            <Field
                label="Cliënt"
                orientation={props.orientation}
                className={props.styles.field}
                validationState={errors.clientPivotId ? 'error' : undefined}
                validationMessage={errors.clientPivotId}
            >
                <FilePartyCombobox
                    disabled={!!values.contributionInvoiceId}
                    entities={props.parties.data || []}
                    fileId={props.file.data.id}
                    filter={party => party.role === (settings.clientRole ?? FilePartyRole.Client)}
                    selectedOptions={[values.clientPivotId].filter(Boolean)}
                    onOptionSelect={(_, data) => setFieldValue('clientPivotId', data.optionValue)}
                />
            </Field>
            <Field
                label={
                    {
                        children: (_, slotProps: LabelProps) => (
                            <InfoLabel {...slotProps} info={'Neem contact op met support om de lijst van arrondissementen aan te passen'}>
                                Arrondissement
                            </InfoLabel>
                        ),
                    } as unknown // The Fluent UI types do not define this properly, therefore suppress any TS errors.
                }
                orientation={props.orientation}
                className={props.styles.field}
                validationState={
                    errors.legalAidBoardPivotId
                        ? 'error'
                        : props.selected.legalAidBoardPartyId && !values.legalAidBoardPivotId
                          ? 'warning'
                          : undefined
                }
                validationMessage={
                    errors.legalAidBoardPivotId ||
                    (props.selected.legalAidBoardPartyId && !values.legalAidBoardPivotId
                        ? 'Dit arrondissement zal als nieuwe partij aan dit dossier worden gekoppeld wanneer u de toevoeging opslaat.'
                        : undefined)
                }
            >
                <LegalAidBoardCombobox
                    disabled={!!values.settlementInvoiceId}
                    dataPromise={props.legalAidBoards}
                    selectedOptions={[props.selected.legalAidBoardPartyId].filter(Boolean)}
                    onOptionSelect={(_, data) => props.setSelected({ ...props.selected, legalAidBoardPartyId: data.optionValue })}
                />
            </Field>
            <Field
                label="Datum aanvraag"
                orientation={props.orientation}
                className={props.styles.field}
                validationState={errors.applied ? 'error' : undefined}
                validationMessage={errors.applied}
            >
                <LocalDatePicker
                    disabled={!!values.settlementInvoiceId}
                    isoValue={values.applied}
                    onSelectDate={date => setFieldValue('applied', date)}
                />
            </Field>
            <Field
                label="Datum toekenning"
                orientation={props.orientation}
                className={props.styles.field}
                validationState={errors.issued ? 'error' : undefined}
                validationMessage={errors.issued}
            >
                <LocalDatePicker
                    disabled={!!values.settlementInvoiceId}
                    isoValue={values.issued}
                    onSelectDate={date => setFieldValue('issued', date)}
                />
            </Field>
            <Field
                label="Kenmerk"
                orientation={props.orientation}
                className={props.styles.field}
                validationState={errors.reference ? 'error' : undefined}
                validationMessage={errors.reference}
            >
                <Input
                    disabled={!!values.settlementInvoiceId}
                    value={values.reference || ''}
                    onChange={(_, data) => setFieldValue('reference', data.value)}
                />
            </Field>
        </div>
    )
}
